import {getTestProps} from '@benchling/js-test-utils';
import {Button, createStyles, Group, Tooltip} from '@mantine/core';
import React from 'react';

import ModelTypeConstants from 'constants/ModelTypeConstants';
import CreateDatasetModal from 'data-frames/modals/CreateDatasetModal';
import SearchInput from 'search/inputs/SearchInput';
import {ZINDEX_MODAL} from 'styles/StyleConstants';
import runModal from 'ui-library/runModal';

const REMOVE_SELECTION_TOOLTIP = 'Remove the selection before uploading a new dataset';

const useStyles = createStyles(() => ({
  // Group sets `flex-grow: 0` on all of its direct children via a `& > *` rule of the same specificity as
  // this class; the doubled selector raises specificity above that so this wrapper still grows to fill
  // the available space.
  searchInputWrapper: {
    '&&': {
      flex: 1,
      minWidth: 0,
    },
  },
  tooltipTarget: {
    display: 'flex',
  },
}));

export interface SelectOrCreateDatasetInputProps {
  id?: string;
  isMulti: boolean;
  selected: Array<string>;
  onSelect: (datasetId: string) => void;
  onRemove: (datasetId: string) => void;
  placeholder?: string;
  disabled?: boolean;
}

/**
 * A `SearchInput` restricted to datasets, with an adjacent "Upload" button that uploads a new dataset (via
 * `CreateDatasetModal`) and selects it.
 *
 * For multi-select fields, the newly created dataset is appended to the selection. For single-select
 * fields, it replaces any existing selection - the "Upload" button is disabled while a dataset is already
 * selected, since there would be nothing to distinguish which dataset the field should hold.
 */
export default function SelectOrCreateDatasetInput({
  id,
  isMulti,
  selected,
  onSelect,
  onRemove,
  placeholder = '',
  disabled = false,
}: SelectOrCreateDatasetInputProps): JSX.Element {
  const {classes} = useStyles();
  const isBlockedBySingleSelection = !isMulti && selected.length > 0;
  const isCreateDisabled = disabled || isBlockedBySingleSelection;
  const disabledTooltip = !disabled && isBlockedBySingleSelection ? REMOVE_SELECTION_TOOLTIP : null;

  async function handleCreateDataset(): Promise<void> {
    const datasetId = await runModal(CreateDatasetModal, {}, {title: 'Create dataset', zIndex: ZINDEX_MODAL});
    if (datasetId == null) {
      return;
    }
    if (!isMulti) {
      selected.forEach((existingId) => {
        onRemove(existingId);
      });
    }
    onSelect(datasetId);
  }

  return (
    <Group noWrap={true} spacing="xs" align="stretch">
      <div className={classes.searchInputWrapper}>
        <SearchInput
          id={id}
          autoFocus={false}
          disableTyping={!isMulti && selected.length > 0}
          disabled={disabled}
          isMulti={isMulti}
          onRemove={(datasetId) => {
            if (datasetId != null) {
              onRemove(datasetId);
            }
          }}
          onSelect={(datasetId) => {
            if (datasetId != null) {
              onSelect(datasetId);
            }
          }}
          placeholder={placeholder}
          selected={selected}
          types={[ModelTypeConstants.DATASET]}
        />
      </div>
      <Tooltip label={disabledTooltip ?? undefined} disabled={disabledTooltip == null}>
        <span className={classes.tooltipTarget}>
          <Button
            h="100%"
            disabled={isCreateDisabled}
            onClick={() => {
              handleCreateDataset();
            }}
            variant="tertiary"
            {...getTestProps({component: 'SelectOrCreateDatasetInput', element: 'createDatasetButton'})}
          >
            Upload
          </Button>
        </span>
      </Tooltip>
    </Group>
  );
}
